import { useState, useRef } from 'react';

interface FotoPerfilModalProps {
  isOpen: boolean;
  onClose: () => void;
  fotoAtual: string;
  nome: string;
  onSalvar: (novaFoto: string) => void;
}

export default function FotoPerfilModal({ isOpen, onClose, fotoAtual, nome, onSalvar }: FotoPerfilModalProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!tiposPermitidos.includes(file.type)) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSalvar = () => {
    if (!preview) return;
    setSalvando(true);
    setTimeout(() => {
      onSalvar(preview);
      setSalvando(false);
      setPreview(null);
      onClose();
    }, 1200);
  };

  const handleFechar = () => {
    setPreview(null);
    onClose();
  };

  const avatarOptions = [
    'https://i.pravatar.cc/150?img=1',
    'https://i.pravatar.cc/150?img=5',
    'https://i.pravatar.cc/150?img=8',
    'https://i.pravatar.cc/150?img=12',
    'https://i.pravatar.cc/150?img=15',
    'https://i.pravatar.cc/150?img=20',
    'https://i.pravatar.cc/150?img=25',
    'https://i.pravatar.cc/150?img=30',
    'https://i.pravatar.cc/150?img=33',
    'https://i.pravatar.cc/150?img=36',
    'https://i.pravatar.cc/150?img=40',
    'https://i.pravatar.cc/150?img=44',
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Foto de Perfil</h3>
              <p className="text-xs text-orange-100 mt-0.5">Escolha ou envie uma nova foto</p>
            </div>
            <button
              onClick={handleFechar}
              className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Preview atual */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <img
                src={preview || fotoAtual}
                alt={nome}
                className="w-24 h-24 rounded-full object-cover border-4 border-orange-200"
              />
              {preview && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                  <i className="ri-check-line text-white text-xs"></i>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-500">
              {preview ? 'Nova foto selecionada' : 'Foto atual'}
            </p>
          </div>

          {/* Upload de arquivo */}
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">Enviar do dispositivo</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm font-medium text-slate-600 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-all cursor-pointer"
            >
              <i className="ri-upload-cloud-line text-lg"></i>
              Selecionar imagem (JPG, PNG, WebP — máx. 5MB)
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Avatares pré-definidos */}
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-3">Ou escolha um avatar</p>
            <div className="grid grid-cols-6 gap-2">
              {avatarOptions.map((avatar, idx) => (
                <button
                  key={idx}
                  onClick={() => setPreview(avatar)}
                  className={`relative rounded-full overflow-hidden border-2 transition-all cursor-pointer ${
                    preview === avatar ? 'border-orange-500 scale-110' : 'border-transparent hover:border-orange-300'
                  }`}
                >
                  <img src={avatar} alt={`Avatar ${idx + 1}`} className="w-full h-full object-cover aspect-square" />
                  {preview === avatar && (
                    <div className="absolute inset-0 bg-orange-500/30 flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs font-bold"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleFechar}
              className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors cursor-pointer text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={!preview || salvando}
              className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {salvando ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Salvar Foto
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
